<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <oxd-table-filter
      :filter-title="$t('attendance.attendance_total_summary_report')"
    >
      <oxd-form @submit-valid="generateReport">
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <employee-autocomplete
                v-model="filters.employee"
                :rules="rules.employee"
                :params="{
                  includeEmployees: 'currentAndPast',
                }"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <jobtitle-dropdown v-model="filters.jobTitle" />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.subunit"
                type="select"
                :label="$t('general.sub_unit')"
                :options="subunits"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <employment-status-dropdown v-model="filters.empStatus" />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.period"
                type="select"
                label="View By"
                :options="periodOptions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="filters.date"
                :rules="rules.date"
                :label="$t('general.date')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.sortField"
                type="select"
                label="Order By"
                :options="sortFieldOptions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.sortOrder"
                type="select"
                :label="$t('admin.order')"
                :options="sortOrderOptions"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <oxd-button
            v-if="reportGenerated"
            type="button"
            display-type="ghost"
            icon-name="download"
            label="Export CSV"
            :disabled="isLoading || isExporting"
            @click="exportReport('csv')"
          />
          <oxd-button
            v-if="reportGenerated"
            type="button"
            display-type="ghost"
            icon-name="download"
            label="Export Excel"
            :disabled="isLoading || isExporting"
            @click="exportReport('xlsx')"
          />
          <oxd-button
            type="submit"
            display-type="secondary"
            :label="$t('general.view')"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>

    <br />

    <div v-if="reportGenerated" class="orangehrm-paper-container">
      <oxd-report-table
        :items="items"
        :headers="matrixHeaders"
        :loading="isLoading"
        :column-count="matrixHeaders.length"
      >
        <template #pagination>
          <oxd-text class="oxd-text--count" tag="span">
            {{ $t('general.n_records_found', {count: total}) }}
          </oxd-text>
          <oxd-pagination
            v-if="showPaginator"
            v-model:current="currentPage"
            :length="pages"
          />
        </template>
        <template #footer>
          <oxd-text class="oxd-text--footer" tag="span">
            {{ $t('time.total_duration') }}:
            {{ response.meta ? response.meta.sum.label : '0.00' }}
          </oxd-text>
        </template>
      </oxd-report-table>
    </div>
  </div>
</template>

<script>
import {computed, ref} from 'vue';
import {
  endOfMonth,
  endOfWeek,
  isSunday,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import {OxdReportTable} from '@ohrm/oxd';
import {
  required,
  validSelection,
  validDateFormat,
} from '@/core/util/validation/rules';
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {freshDate, formatDate, parseDate} from '@/core/util/helper/datefns';
import JobtitleDropdown from '@/orangehrmPimPlugin/components/JobtitleDropdown';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import EmploymentStatusDropdown from '@/orangehrmPimPlugin/components/EmploymentStatusDropdown';
import usei18n from '@/core/util/composable/usei18n';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {
  createCsv,
  createXlsx,
  downloadFile,
} from '@/orangehrmAttendancePlugin/util/attendance-report-export';

const getPeriodRange = (date, period) => {
  const referenceDate = parseDate(date);
  if (!referenceDate) {
    return {fromDate: null, toDate: null};
  }

  const isWeek = period === 'week';
  const fromDate = isWeek
    ? startOfWeek(referenceDate, {weekStartsOn: 1})
    : startOfMonth(referenceDate);
  const toDate = isWeek
    ? endOfWeek(referenceDate, {weekStartsOn: 1})
    : endOfMonth(referenceDate);

  return {
    fromDate: formatDate(fromDate, 'yyyy-MM-dd'),
    toDate: formatDate(toDate, 'yyyy-MM-dd'),
  };
};

export default {
  name: 'AttendanceSummaryReport',

  components: {
    'oxd-report-table': OxdReportTable,
    'jobtitle-dropdown': JobtitleDropdown,
    'employee-autocomplete': EmployeeAutocomplete,
    'employment-status-dropdown': EmploymentStatusDropdown,
  },

  props: {
    subunits: {
      type: Array,
      default: () => [],
    },
  },

  setup() {
    const {$t} = usei18n();
    const {userDateFormat} = useDateFormat();
    const periodOptions = [
      {id: 'week', label: 'Week'},
      {id: 'month', label: 'Month'},
    ];
    const sortFieldOptions = [
      {id: 'employee.lastName', label: $t('general.employee_name')},
      {id: 'punchInTime', label: $t('attendance.punch_in')},
      {id: 'punchOutTime', label: $t('attendance.punch_out')},
      {id: 'total', label: $t('attendance.duration_hours')},
    ];
    const sortOrderOptions = [
      {id: 'ASC', label: $t('general.ascending')},
      {id: 'DESC', label: $t('general.descending')},
    ];
    const filters = ref({
      employee: null,
      jobTitle: null,
      subunit: null,
      empStatus: null,
      period: periodOptions[0],
      date: formatDate(freshDate(), 'yyyy-MM-dd'),
      sortField: sortFieldOptions[0],
      sortOrder: sortOrderOptions[0],
    });
    const reportGenerated = ref(false);
    const isExporting = ref(false);
    const rules = {
      date: [required, validDateFormat(userDateFormat)],
      employee: [validSelection],
    };
    const periodRange = computed(() =>
      getPeriodRange(filters.value.date, filters.value.period?.id),
    );
    const serializedFilters = computed(() => ({
      name: 'attendance',
      _dateFormattingEnabled: true,
      empNumber: filters.value.employee?.id,
      fromDate: periodRange.value.fromDate,
      toDate: periodRange.value.toDate,
      jobTitleId: filters.value.jobTitle?.id,
      subunitId: filters.value.subunit?.id,
      employmentStatusId: filters.value.empStatus?.id,
      sortField: filters.value.sortField?.id,
      sortOrder: filters.value.sortOrder?.id,
    }));
    const expectedDays = computed(() => response.value.meta?.days ?? []);
    const getEmptyDayLabel = (day, meta) => {
      const status = meta?.dayStatuses?.[day];
      if (status?.type === 'holiday') {
        const holidayType =
          status.length === 'halfDay' ? 'Half-Day Holiday' : 'Holiday';
        return status.name ? `${holidayType} (${status.name})` : holidayType;
      }
      if (isSunday(parseDate(day))) {
        return 'Rest';
      }
      return status?.type === 'offDay' ? 'Off Day' : '-';
    };
    const getDayValue = (employee, day, meta) => {
      const attendance = employee.daily?.[day];
      return attendance
        ? `${attendance.punchIn} – ${attendance.punchOut} (${attendance.hours})`
        : getEmptyDayLabel(day, meta);
    };
    const normalizer = (data) =>
      data.map((employee) => {
        const row = {
          employeeName: employee.employeeName,
          totalHours: employee.time,
          _rows: 0,
        };
        expectedDays.value.forEach((day) => {
          row[`day_${day}`] = getDayValue(employee, day, response.value.meta);
        });
        return row;
      });
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/time/reports/data',
    );
    const {
      total,
      pages,
      response,
      isLoading,
      execQuery,
      currentPage,
      showPaginator,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer,
      prefetch: false,
    });
    const matrixHeaders = computed(() => [
      {
        name: $t('general.employee_name'),
        prop: 'employeeName',
        pin: 'colPinStart',
      },
      ...expectedDays.value.map((day) => ({
        name: formatDate(parseDate(day), 'EEE dd'),
        prop: `day_${day}`,
      })),
      {
        name: $t('time.total_duration'),
        prop: 'totalHours',
        pin: 'colPinEnd',
      },
    ]);
    const items = computed(() => response.value.data ?? []);
    const generateReport = async () => {
      await execQuery();
      reportGenerated.value = true;
    };
    const exportReport = async (fileType) => {
      isExporting.value = true;
      try {
        const {data} = await http.getAll({
          ...serializedFilters.value,
          limit: 0,
          offset: 0,
        });
        const days = data.meta?.days ?? [];
        const rows = [
          [
            $t('general.employee_name'),
            ...days.map((day) => formatDate(parseDate(day), 'EEE dd MMM yyyy')),
            $t('time.total_duration'),
          ],
          ...data.data.map((employee) => [
            employee.employeeName,
            ...days.map((day) => getDayValue(employee, day, data.meta)),
            employee.time,
          ]),
        ];
        const filename = `attendance-summary-${filters.value.period?.id}-${periodRange.value.fromDate}-to-${periodRange.value.toDate}`;
        if (fileType === 'xlsx') {
          downloadFile(
            createXlsx(rows),
            `${filename}.xlsx`,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          );
        } else {
          downloadFile(
            createCsv(rows),
            `${filename}.csv`,
            'text/csv;charset=utf-8',
          );
        }
      } finally {
        isExporting.value = false;
      }
    };

    return {
      total,
      pages,
      items,
      response,
      filters,
      rules,
      isLoading,
      isExporting,
      currentPage,
      showPaginator,
      reportGenerated,
      matrixHeaders,
      periodOptions,
      sortFieldOptions,
      sortOrderOptions,
      generateReport,
      exportReport,
    };
  },
};
</script>

<style
  src="../../core/components/table/reports-table.scss"
  lang="scss"
  scoped
></style>
